import { Typography } from '@mui/material';
import type { AgBarSeriesOptions } from 'ag-charts-enterprise';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';

import StackedBarChart from '@/components/charts/StackedBarChart';
import { PRIORITIES } from '@/constants/common';
import { YEAR_MONTH_DAY_FORMAT } from '@/constants/date.constants';
import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import getDates from '@/pages/Dashboard/Dashboard.utils';
import STACKED_BAR_SERIES from '@/pages/Dashboard/DashboardTickets/DashboardTickets.constants';
import type { Ticket } from '@/pages/Tickets';

import {
  reportsEndDateAtom,
  reportsTimeFrameAtom
} from '../../ReportsTemplate.hooks';
import useStyles from '../ReportsPages.styles';

const TicketsPage1 = ({ tickets }: { tickets: Ticket[] }) => {
  const { classes } = useStyles();
  const [diff] = useAtom(reportsTimeFrameAtom);
  const [endDate] = useAtom(reportsEndDateAtom);
  const minTimeFrame = diff < 7 ? 7 : diff;

  const threatTicketPrioritiesDaysGroupBy = () => {
    const dates = getDates(dayjs(endDate), minTimeFrame - 1);

    // Create an array of objects, one for each date
    return dates.map((date: string) => {
      // Initialize object with all required properties
      const dateObj: { [key: string]: number | Date } = {
        date: dayjs(date).toDate(),
        P1: 0,
        P2: 0,
        P3: 0,
        P4: 0,
        P5: 0
      };

      // For each priority, count the tickets for this date
      PRIORITIES.forEach((priority) => {
        const ticketsByDateAndPriority = tickets.filter(
          (ticket: Ticket) =>
            ticket.ticket_priority?.name === priority &&
            dayjs
              .utc(ticket.published_at)
              .local()
              .format(YEAR_MONTH_DAY_FORMAT) === date
        );
        dateObj[priority] = ticketsByDateAndPriority.length;
      });

      return dateObj;
    });
  };

  return (
    <div className={classes.reportsPageContainer}>
      <ReportParagraph>
        {REPORT_PARAGRAPHS.threatTicketsDescription}
      </ReportParagraph>
      <ReportParagraph>
        {REPORT_PARAGRAPHS.threatTicketsDetails}
      </ReportParagraph>
      <div
        style={{ height: 300, width: '100%' }}
        className={classes.reportsChartContainer}
        data-testid="new-threat-tickets-per-day"
      >
        <Typography className={classes.reportsPreviewSubTitle}>
          New Threat Tickets Per Day
        </Typography>
        <StackedBarChart
          data={threatTicketPrioritiesDaysGroupBy()}
          series={STACKED_BAR_SERIES as AgBarSeriesOptions[]}
        />
      </div>
      <ReportParagraph fontSize="12px">
        {REPORT_PARAGRAPHS.threatTicketsPerDayDescription}
      </ReportParagraph>
    </div>
  );
};
export default TicketsPage1;
